import {memo, useCallback, useEffect, useMemo} from "react";
import {AlertOperatorDropdownProps} from "./alertOperatorDropdown.typings";
import {Condition_SecurityAlertCondition_Operator} from "../../../proto/generated/nexus";
import {MenuItem, Select, SelectChangeEvent} from "@mui/material";
import { alertModalMenuProps } from "../alertModal";

const LINE_OPERATORS: Condition_SecurityAlertCondition_Operator[] = [
    Condition_SecurityAlertCondition_Operator.CROSSING,
    Condition_SecurityAlertCondition_Operator.CROSSING_UP,
    Condition_SecurityAlertCondition_Operator.CROSSING_DOWN,
    Condition_SecurityAlertCondition_Operator.EQUAL_TO,
    Condition_SecurityAlertCondition_Operator.GREATER_THAN,
    Condition_SecurityAlertCondition_Operator.LESS_THAN
]

/** TradingView Fib alert operators (no Equal To). */
const FIBONACCI_OPERATORS: Condition_SecurityAlertCondition_Operator[] = [
    Condition_SecurityAlertCondition_Operator.CROSSING,
    Condition_SecurityAlertCondition_Operator.CROSSING_UP,
    Condition_SecurityAlertCondition_Operator.CROSSING_DOWN,
    Condition_SecurityAlertCondition_Operator.GREATER_THAN,
    Condition_SecurityAlertCondition_Operator.LESS_THAN,
    Condition_SecurityAlertCondition_Operator.ENTERING,
    Condition_SecurityAlertCondition_Operator.EXITING,
    Condition_SecurityAlertCondition_Operator.INSIDE,
    Condition_SecurityAlertCondition_Operator.OUTSIDE,
]

const RECTANGLE_OPERATORS: Condition_SecurityAlertCondition_Operator[] = [
    Condition_SecurityAlertCondition_Operator.ENTERING,
    Condition_SecurityAlertCondition_Operator.EXITING,
    Condition_SecurityAlertCondition_Operator.INSIDE,
    Condition_SecurityAlertCondition_Operator.OUTSIDE,
]

const ZONE_OPERATORS: Condition_SecurityAlertCondition_Operator[] = [
    Condition_SecurityAlertCondition_Operator.ENTERING,
    Condition_SecurityAlertCondition_Operator.EXITING,
    Condition_SecurityAlertCondition_Operator.INSIDE,
    Condition_SecurityAlertCondition_Operator.OUTSIDE,
]

export function isZoneOperator(operator: Condition_SecurityAlertCondition_Operator | undefined | null): boolean {
    return Boolean(
        operator != null &&
        (ZONE_OPERATORS as readonly Condition_SecurityAlertCondition_Operator[]).includes(operator)
    )
}

/** @deprecated Use isZoneOperator */
export const isRectangleZoneOperator = isZoneOperator

export function getOperatorsForDrawingType(drawingType: string | undefined | null): Condition_SecurityAlertCondition_Operator[] {
    if (drawingType === 'rectangle') return RECTANGLE_OPERATORS
    if (drawingType === 'fibonacci') return FIBONACCI_OPERATORS
    return LINE_OPERATORS
}

export function getDefaultOperatorForDrawingType(drawingType: string | undefined | null): Condition_SecurityAlertCondition_Operator {
    if (drawingType === 'rectangle') return Condition_SecurityAlertCondition_Operator.ENTERING
    return Condition_SecurityAlertCondition_Operator.CROSSING
}

export function getOperatorDisplayName(
    operator: Condition_SecurityAlertCondition_Operator,
    drawingType?: string | null
): string {
    switch (operator) {
        case Condition_SecurityAlertCondition_Operator.CROSSING:
            return 'Crossing'
        case Condition_SecurityAlertCondition_Operator.CROSSING_UP:
            return 'Crossing Up'
        case Condition_SecurityAlertCondition_Operator.CROSSING_DOWN:
            return 'Crossing Down'
        case Condition_SecurityAlertCondition_Operator.EQUAL_TO:
            return 'Equal To'
        case Condition_SecurityAlertCondition_Operator.GREATER_THAN:
            return 'Greater Than'
        case Condition_SecurityAlertCondition_Operator.LESS_THAN:
            return 'Less Than'
        case Condition_SecurityAlertCondition_Operator.ENTERING:
            return drawingType === 'rectangle' ? 'Entering rectangle' : 'Entering Channel'
        case Condition_SecurityAlertCondition_Operator.EXITING:
            return drawingType === 'rectangle' ? 'Exiting rectangle' : 'Exiting Channel'
        case Condition_SecurityAlertCondition_Operator.INSIDE:
            return drawingType === 'rectangle' ? 'Inside rectangle' : 'Inside Channel'
        case Condition_SecurityAlertCondition_Operator.OUTSIDE:
            return drawingType === 'rectangle' ? 'Outside rectangle' : 'Outside Channel'
        default:
            return ''
    }
}

/**
 * Static display map used by list rows that don't have drawing context.
 * Zone ops default to Channel wording (Fib); rectangle messages should pass drawingType.
 */
export const operatorDisplayNames: Record<Condition_SecurityAlertCondition_Operator, string> = {
    [Condition_SecurityAlertCondition_Operator.CROSSING]: 'Crossing',
    [Condition_SecurityAlertCondition_Operator.CROSSING_UP]: 'Crossing Up',
    [Condition_SecurityAlertCondition_Operator.CROSSING_DOWN]: 'Crossing Down',
    [Condition_SecurityAlertCondition_Operator.EQUAL_TO]: 'Equal To',
    [Condition_SecurityAlertCondition_Operator.GREATER_THAN]: 'Greater Than',
    [Condition_SecurityAlertCondition_Operator.LESS_THAN]: 'Less Than',
    [Condition_SecurityAlertCondition_Operator.ENTERING]: 'Entering Channel',
    [Condition_SecurityAlertCondition_Operator.EXITING]: 'Exiting Channel',
    [Condition_SecurityAlertCondition_Operator.INSIDE]: 'Inside Channel',
    [Condition_SecurityAlertCondition_Operator.OUTSIDE]: 'Outside Channel',
    [Condition_SecurityAlertCondition_Operator.OPERATOR_UNKNOWN]: '',
    [Condition_SecurityAlertCondition_Operator.UNRECOGNIZED]: '',
}

const AlertOperatorDropdown: React.FC<AlertOperatorDropdownProps> = ({onOperatorChange, value, className, drawingType}) => {
    const availableOperators = useMemo(() => getOperatorsForDrawingType(drawingType), [drawingType])

    const menuItems = useMemo(() => {
        return availableOperators.map(v => ({
            label: getOperatorDisplayName(v, drawingType),
            value: v,
        }))
    }, [availableOperators, drawingType])

    const onSelect = useCallback((event: SelectChangeEvent<Condition_SecurityAlertCondition_Operator>) => {
        onOperatorChange(event.target.value as Condition_SecurityAlertCondition_Operator)
    }, [onOperatorChange])

    useEffect(() => {
        if (!availableOperators.includes(value) && availableOperators[0] != null) {
            onOperatorChange(availableOperators[0])
        }
    }, [availableOperators, value, onOperatorChange])

    const selectValue = availableOperators.includes(value) ? value : availableOperators[0]

    return <Select fullWidth size="small" value={selectValue} onChange={onSelect} className={className} MenuProps={alertModalMenuProps}>
        {menuItems.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
    </Select>
}

export default memo(AlertOperatorDropdown);
